/**
 * Add to Cart V2 - Vanilla JS Implementation
 * Product page add to cart functionality
 */

class AddToCartV2 {
  constructor(options = {}) {
    this.productId = options.productId || null;
    this.sectionId = options.sectionId || null;
    this.productUrl = options.productUrl || null;
    this.buttonText = options.buttonText || 'Add to cart';
    this.initialAvailable = options.initialAvailable !== undefined ? options.initialAvailable : true;
    this.isLoading = false;
    this.quantity = 1;
    this.selectedAccessories = [];
    this.selectedGifts = [];
    
    // Dynamic element IDs
    this.formId = options.formId || 'product-form-add-to-cart';
    this.buttonId = options.buttonId || 'add-to-cart-btn';
    this.quantityId = options.quantityId || 'quantity-input';
    this.selectId = options.selectId || 'product-select';
    
    this.init();
  }
  
  init() {
    console.log('AddToCartV2 initialized');
    this.bindEvents();
    this.checkInitialVariantAvailability();
  }
  
  bindEvents() {
    const addToCartBtn = document.getElementById(this.buttonId);
    const quantityInput = document.getElementById(this.quantityId);
    
    // Prevent form from submitting (we handle it via AJAX)
    const productForm = document.querySelector('.product-single__form');
    if (productForm) {
      productForm.addEventListener('submit', (e) => {
        console.log('AddToCartV2: Form submit intercepted, preventing default');
        e.preventDefault();
        e.stopPropagation();
      }, true);
    }
    
    // Listen to accessories change event
    document.addEventListener('accessories-change', (e) => {
      console.log('accessories-change event received:', e.detail);
      const selectedItems = e.detail.selectedItems || [];
      this.selectedAccessories = selectedItems.filter(item => item.type === 'accessory');
      console.log('Selected accessories:', this.selectedAccessories);
    });
    
    // Listen to variant change events
    const variantCards = document.querySelector('variant-cards-v2');
    if (variantCards) {
      variantCards.addEventListener('variant-change', (e) => {
        this.updateButtonAvailability(e.detail.available);
      });
    }
    
    // Initialize accessories
    setTimeout(() => {
      this.initializeAccessoriesAndGifts();
    }, 100);
    
    // Button click (not form submit to avoid external JS interference)
    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        if (addToCartBtn.disabled && !this.isLoading) {
          return;
        }
        
        const productSelect = document.getElementById(this.selectId);
        const variantId = productSelect ? productSelect.value : null;
        const qty = quantityInput ? quantityInput.value : this.quantity;
        
        if (!variantId) {
          return;
        }
        
        this.addToCart(parseInt(variantId), parseInt(qty));
      }, true); // Use capture phase to ensure we intercept first
    }
  }
  
  checkInitialVariantAvailability() {
    console.log('checkInitialVariantAvailability called');
    console.log('initialAvailable:', this.initialAvailable);
    
    // Check variant-cards-v2 for multi-variant products
    const variantCards = document.querySelector('variant-cards-v2');
    
    if (variantCards && variantCards.variants && Array.isArray(variantCards.variants)) {
      const selectedVariantId = variantCards.selectedVariantId || 
        (typeof variantCards.getSelectedVariantId === 'function' ? variantCards.getSelectedVariantId() : null);
      
      if (selectedVariantId) {
        const selectedVariant = variantCards.variants.find(v => v.id == selectedVariantId);
        if (selectedVariant) {
          this.updateButtonAvailability(selectedVariant.available);
          return;
        }
      }
    }
    
    // Fallback to initialAvailable
    this.updateButtonAvailability(this.initialAvailable);
  }
  
  updateButtonAvailability(isAvailable) {
    console.log('updateButtonAvailability called with isAvailable:', isAvailable);
    const addToCartBtn = document.getElementById(this.buttonId);
    if (!addToCartBtn) return;
    
    if (isAvailable) {
      addToCartBtn.disabled = this.isLoading;
      addToCartBtn.innerHTML = this.getButtonHTML(this.isLoading ? 'Adding...' : this.buttonText);
    } else {
      addToCartBtn.disabled = true;
      addToCartBtn.innerHTML = this.getButtonHTML('Sold Out');
    }
  }
  
  getButtonHTML(text) {
    return `
      ${text}
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M6 6h15l-1.5 9h-12z"/>
        <circle cx="9" cy="20" r="1"/>
        <circle cx="18" cy="20" r="1"/>
        <path d="M6 6L4 2H1"/>
      </svg>
    `;
  }
  
  async addToCart(variantId, quantity) {
    const addToCartBtn = document.getElementById(this.buttonId);
    if (!addToCartBtn) return;
    
    if (this.isLoading) {
      console.warn('addToCart already in progress');
      return;
    }
    
    console.log('addToCart called with:', { variantId, quantity });
    
    this.isLoading = true;
    addToCartBtn.disabled = true;
    addToCartBtn.innerHTML = this.getButtonHTML('Adding...');
    
    try {
      const sectionsUrl = this.productUrl ? this.productUrl.replace(/^https?:\/\/[^\/]+/, '') : '';
      
      // Get cart drawer sections
      const cartDrawer = document.querySelector('cart-drawer');
      let sections = [];
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        try {
          const sectionsToRender = cartDrawer.getSectionsToRender();
          if (sectionsToRender && Array.isArray(sectionsToRender)) {
            sections = sectionsToRender.map((section) => section.id);
          }
        } catch (e) {
          console.error('Error getting sections to render:', e);
        }
      }
      
      // 1. Add main product
      await this.addMainProduct(variantId, quantity);
      
      // 2. Add accessories
      const additionalItems = [];
      if (this.selectedAccessories && this.selectedAccessories.length > 0) {
        this.selectedAccessories.forEach(accessory => {
          if (accessory.id === variantId) {
            console.warn('Skipping accessory matching main product');
            return;
          }
          additionalItems.push({
            id: accessory.id,
            quantity: 1,
            properties: {
              _is_accessory: true,
              'Accessory Name': accessory.title || ''
            }
          });
        });
      }
      
      if (additionalItems.length > 0) {
        await this.addMultipleItems(additionalItems);
      }
      
      // 3. Update cart drawer
      let updateResponse = null;
      if (sections && sections.length > 0) {
        await new Promise(resolve => setTimeout(resolve, 300));
        updateResponse = await this.updateCartWithSections(sections, sectionsUrl);
      }
      
      if (cartDrawer && typeof cartDrawer.renderContents === 'function' && updateResponse && updateResponse.sections) {
        cartDrawer.renderContents(updateResponse);
      }
      
      // 4. Update cart count
      const cartResponse = await fetch('/cart.json', {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });
      const cartData = await cartResponse.json();
      this.updateCartCount(cartData.item_count);
      
      // // 5. Show success notification
      // this.showSuccessNotification();
      
      // // 6. Dispatch success event
      // document.dispatchEvent(new CustomEvent('add-to-cart-success', {
      //   detail: { cartCount: cartData.item_count, variantId, quantity }
      // }));
      
    } catch (error) {
      console.error('Error adding to cart:', error);
      document.dispatchEvent(new CustomEvent('add-to-cart-error', {
        detail: { error: error.message }
      }));
    } finally {
      this.isLoading = false;
      this.checkInitialVariantAvailability();
    }
  }
  
  async addMainProduct(variantId, quantity) {
    const formData = new FormData();
    
    const productForm = document.querySelector('.product-single__form');
    if (productForm) {
      const hiddenInputs = productForm.querySelectorAll('input[type="hidden"]');
      hiddenInputs.forEach(input => {
        if (input.name && input.value && !input.disabled) {
          formData.append(input.name, input.value);
        }
      });
    }
    
    formData.set('id', variantId);
    formData.set('quantity', quantity);
    
    console.log('Adding main product with form-data:', Array.from(formData.entries()));
    
    const response = await fetch('/cart/add.json', {
      method: 'POST',
      body: formData
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.description || errorData.message || 'Failed to add main product');
    }
    
    return await response.json();
  }
  
  async addMultipleItems(items) {
    console.log('Adding multiple items:', items);
    
    const response = await fetch('/cart/add.json', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      console.warn('Warning: Failed to add items:', errorData);
    }
    
    return response.ok ? await response.json() : null;
  }
  
  async updateCartWithSections(sections, sectionsUrl) {
    if (!sections || sections.length === 0) return null;
    
    const formData = new FormData();
    formData.append('sections', sections.join(','));
    formData.append('sections_url', sectionsUrl || window.location.pathname);
    
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        return await response.json();
      }
    } catch (e) {
      console.error('Error calling /cart/update.js:', e);
    }
    
    return null;
  }
  
  updateCartCount(cartCount) {
    const cartCountBubbles = document.querySelectorAll('.cart-count-bubble');
    cartCountBubbles.forEach(bubble => {
      const countSpan = bubble.querySelector('span[aria-hidden="true"]');
      if (countSpan) countSpan.textContent = cartCount;
    });
    
    if (cartCount > 0 && cartCountBubbles.length === 0) {
      const cartLinks = document.querySelectorAll('a[href="/cart"]');
      cartLinks.forEach(cartLink => {
        const existingBubble = cartLink.querySelector('.cart-count-bubble');
        if (!existingBubble) {
          const bubble = document.createElement('div');
          bubble.className = 'cart-count-bubble';
          const countSpan = document.createElement('span');
          countSpan.setAttribute('aria-hidden', 'true');
          countSpan.textContent = cartCount;
          bubble.appendChild(countSpan);
          
          const visuallyHiddenSpan = document.createElement('span');
          visuallyHiddenSpan.className = 'visually-hidden';
          visuallyHiddenSpan.textContent = cartCount + ' items in cart';
          bubble.appendChild(visuallyHiddenSpan);
          
          cartLink.appendChild(bubble);
        }
      });
    }
    
    const otherCartCountElements = document.querySelectorAll('[data-cart-count], .cart-count, .cart-item-count');
    otherCartCountElements.forEach(element => {
      element.textContent = cartCount;
    });
  }
  
  showSuccessNotification() {
    const notification = document.getElementById('add-to-cart-success');
    if (notification) {
      notification.classList.add('show');
      setTimeout(() => {
        notification.classList.remove('show');
      }, 3000);
    }
  }
  
  initializeAccessoriesAndGifts() {
    const accessoriesSection = document.querySelector('accessories-section-v2');
    if (accessoriesSection && typeof accessoriesSection.getSelectedItems === 'function') {
      try {
        const items = accessoriesSection.getSelectedItems();
        this.selectedAccessories = items.filter(item => item.type === 'accessory');
        console.log('Initialized accessories from section:', this.selectedAccessories);
      } catch (e) {
        console.error('Error initializing accessories:', e);
      }
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // Main product page add to cart
  const addToCartContainer = document.getElementById('add-to-cart-container');
  if (addToCartContainer) {
    window.addToCartInstance = new AddToCartV2({
      productId: addToCartContainer.dataset.productId,
      sectionId: addToCartContainer.dataset.sectionId,
      productUrl: addToCartContainer.dataset.productUrl,
      buttonText: addToCartContainer.dataset.buttonText || 'Add to cart',
      initialAvailable: addToCartContainer.dataset.initialAvailable !== 'false',
      formId: 'product-form-add-to-cart',
      buttonId: 'add-to-cart-btn',
      quantityId: 'quantity-input',
      selectId: 'product-select'
    });
  }
  
  // Quick nav bar add to cart
  const quickNavContainer = document.getElementById('quick-nav-add-to-cart-container');
  if (quickNavContainer) {
    const sectionId = quickNavContainer.dataset.sectionId;
    window.quickNavAddToCartInstance = new AddToCartV2({
      productId: quickNavContainer.dataset.productId,
      sectionId: sectionId,
      productUrl: quickNavContainer.dataset.productUrl,
      buttonText: quickNavContainer.dataset.buttonText || 'Add to cart',
      initialAvailable: quickNavContainer.dataset.initialAvailable !== 'false',
      formId: 'quick-nav-product-form-' + sectionId,
      buttonId: 'quick-nav-btn-' + sectionId,
      quantityId: 'quick-nav-quantity-input-' + sectionId,
      selectId: 'quick-nav-product-select-' + sectionId
    });
  }
});